import React, { useState } from "react";
import { Upload, Loader2, X, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { uploadToCloudinary, cloudinaryThumb } from "@/lib/cloudinary";
import { toast } from "sonner";

const TIPOS_CARNET = ["B", "B+E", "C1", "C1+E", "C", "C+E", "D1", "D"];

/**
 * Formulario reutilizable para los datos de carnet de conducir.
 *
 * Props:
 *  - value: { tiene, tipo, numero, fecha_caducidad, foto_anverso_url, foto_reverso_url, notas }
 *  - onChange: (newValue) => void
 *  - disabled?: bool
 */
export default function CarnetConducirForm({ value, onChange, disabled = false }) {
  const v = value || {};
  const [uploading, setUploading] = useState(null);

  const set = (patch) => onChange({ ...v, ...patch });

  const subirFoto = async (lado, file) => {
    if (!file) return;
    setUploading(lado);
    try {
      const up = await uploadToCloudinary(file, "dnf/carnets", "image");
      set({ [`foto_${lado}_url`]: up.secure_url });
      toast.success(`Foto ${lado === "anverso" ? "anverso" : "reverso"} subida`);
    } catch (e) {
      toast.error(e.message || "Error subiendo foto");
    } finally { setUploading(null); }
  };

  return (
    <div className="bg-zinc-900/40 border border-zinc-800 rounded-lg p-4 space-y-3" data-testid="carnet-form">
      <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
        <input
          type="checkbox"
          checked={!!v.tiene}
          onChange={(e) => set({ tiene: e.target.checked })}
          disabled={disabled}
          data-testid="carnet-tiene"
          className="w-4 h-4 accent-amber-500"
        />
        <span>🚗 Tiene carnet de conducir</span>
        <span className="text-xs text-zinc-500 ml-auto">Sin carnet → no puede conducir vehículos DNF</span>
      </label>

      {v.tiene && (
        <div className="space-y-3 pl-2 border-l-2 border-amber-500/40">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500">Tipo</label>
              <select value={v.tipo || ""} onChange={(e) => set({ tipo: e.target.value || null })}
                disabled={disabled} data-testid="carnet-tipo"
                className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-sm">
                <option value="">— Selecciona —</option>
                {TIPOS_CARNET.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500">Número</label>
              <input value={v.numero || ""} onChange={(e) => set({ numero: e.target.value || null })}
                disabled={disabled} data-testid="carnet-numero" placeholder="12345678X"
                className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500">Caduca</label>
              <input type="date" value={v.fecha_caducidad || ""} onChange={(e) => set({ fecha_caducidad: e.target.value || null })}
                disabled={disabled} data-testid="carnet-caducidad"
                className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-sm" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {["anverso", "reverso"].map((lado) => {
              const url = v[`foto_${lado}_url`];
              return (
                <div key={lado}>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Foto {lado}</label>
                  {url ? (
                    <div className="relative">
                      <img src={cloudinaryThumb(url, 280, 180)} alt={`Carnet ${lado}`}
                        className="w-full h-28 object-cover rounded border border-zinc-800" />
                      <button onClick={() => set({ [`foto_${lado}_url`]: null })} disabled={disabled}
                        data-testid={`carnet-foto-${lado}-quitar`}
                        className="absolute top-1 right-1 bg-rose-500 hover:bg-rose-600 text-white rounded-full p-1">
                        <X className="w-3 h-3" />
                      </button>
                      <div className="absolute bottom-1 left-1 bg-emerald-500/90 text-zinc-900 text-[9px] font-bold rounded px-1.5 py-0.5 flex items-center gap-0.5">
                        <CheckCircle2 className="w-2.5 h-2.5" /> {lado}
                      </div>
                    </div>
                  ) : (
                    <label className="cursor-pointer">
                      <input type="file" accept="image/*" capture="environment" className="hidden"
                        onChange={(e) => subirFoto(lado, e.target.files?.[0])}
                        disabled={disabled || uploading}
                        data-testid={`carnet-foto-${lado}-input`} />
                      <div className="h-28 bg-zinc-950 border-2 border-dashed border-zinc-700 hover:border-amber-500/50 rounded flex flex-col items-center justify-center text-xs text-zinc-500 hover:text-amber-300 transition">
                        {uploading === lado ? (
                          <Loader2 className="w-5 h-5 animate-spin text-amber-500" />
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mb-1" />
                            <span>Foto {lado}</span>
                          </>
                        )}
                      </div>
                    </label>
                  )}
                </div>
              );
            })}
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500">Notas</label>
            <textarea value={v.notas || ""} onChange={(e) => set({ notas: e.target.value || null })}
              disabled={disabled} data-testid="carnet-notas" rows={2}
              placeholder="Observaciones (ej: restricciones, fecha del último canje, etc.)"
              className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-sm" />
          </div>
        </div>
      )}
    </div>
  );
}
